import { ArrowRight, Check } from 'lucide-react';
import { CONTACT_EMAIL, PORTAL_URL } from '@/lib/config';
import { t, useLang, type StringTable } from '@/lib/i18n';

const STRINGS = {
  'pricing.eyebrow': { en: 'Engagement', fa: 'روالِ همکاری' },
  'pricing.title.lead': {
    en: 'Two ways to run the cup —',
    fa: 'دو روش برای به‌کارگیریِ جام —',
  },
  'pricing.title.accent': {
    en: 'your data, your perimeter',
    fa: 'دادهٔ شما، محدودهٔ شما',
  },
  'pricing.title.tail': { en: '.', fa: '.' },
  'pricing.subtitle': {
    en: 'Begin on the hosted platform while we onboard a small cohort of analyst teams. When the work cannot leave the building, we ship the entire stack inside it.',
    fa: 'با میزبانیِ ما آغاز کنید — درحالی‌که گروهِ کوچکی از تیم‌های تحلیلی را پذیرش می‌کنیم. هنگامی که کار نتواند از ساختمان بیرون رود، تمامِ سامانه را در داخلِ آن مستقر می‌کنیم.',
  },
  'pricing.recommended': { en: 'Recommended', fa: 'پیشنهادی' },
  'pricing.tier.analyst.name': { en: 'Analyst', fa: 'تحلیل‌گر' },
  'pricing.tier.analyst.tagline': {
    en: 'For teams doing the daily work of intelligence.',
    fa: 'برای تیم‌هایی که کارِ روزمرّهٔ اطلاعاتی را پیش می‌برند.',
  },
  'pricing.tier.analyst.price': { en: 'Contact', fa: 'تماس' },
  'pricing.tier.analyst.note': {
    en: 'per seat · billed annually',
    fa: 'هر صندلی · صورتحساب سالانه',
  },
  'pricing.tier.analyst.cta': { en: 'Request access', fa: 'درخواست دسترسی' },
  'pricing.tier.analyst.f1': {
    en: 'Hosted platform with managed updates',
    fa: 'میزبانیِ مدیریت‌شده با به‌روزرسانیِ خودکار',
  },
  'pricing.tier.analyst.f2': {
    en: 'Graph-native retrieval and streaming synthesis',
    fa: 'بازیابیِ گرافی و هم‌نهادسازیِ جاری',
  },
  'pricing.tier.analyst.f3': {
    en: 'Provenance trail on every claim',
    fa: 'ردِ سندیّت بر هر ادعا',
  },
  'pricing.tier.analyst.f4': {
    en: 'Bilingual UI — Persian and English',
    fa: 'رابطِ کاربری دوزبانه — فارسی و انگلیسی',
  },
  'pricing.tier.analyst.f5': {
    en: 'Command palette, dual themes, role-based access',
    fa: 'پنل فرمان، دو پوسته، دسترسیِ نقش‌بنیان',
  },
  'pricing.tier.analyst.f6': {
    en: 'Audit log, SOC 2 controls, SSO',
    fa: 'گزارشِ ممیّزی، کنترل‌های SOC 2، ورود یکپارچه',
  },
  'pricing.tier.sovereign.name': { en: 'Sovereign', fa: 'مستقل' },
  'pricing.tier.sovereign.tagline': {
    en: 'For organizations whose data cannot leave the perimeter.',
    fa: 'برای سازمان‌هایی که دادهٔ آن‌ها نباید از محدوده خارج شود.',
  },
  'pricing.tier.sovereign.price': { en: 'Custom', fa: 'سفارشی' },
  'pricing.tier.sovereign.note': {
    en: 'self-hosted · annual license',
    fa: 'خود-میزبان · مجوزِ سالانه',
  },
  'pricing.tier.sovereign.cta': { en: 'Talk to us', fa: 'با ما گفتگو کنید' },
  'pricing.tier.sovereign.f1': {
    en: 'Self-hosted in your VPC or air-gap',
    fa: 'استقرار در شبکهٔ خصوصی یا محیطِ ایزوله',
  },
  'pricing.tier.sovereign.f2': {
    en: 'Bring-your-own model — Azure, Bedrock, on-prem',
    fa: 'مدلِ خود را بیاورید — Azure، Bedrock، یا داخلی',
  },
  'pricing.tier.sovereign.f3': {
    en: 'Private graph database, private embeddings',
    fa: 'پایگاه‌دادهٔ گرافیِ خصوصی، تعبیه‌های خصوصی',
  },
  'pricing.tier.sovereign.f4': {
    en: 'Hardened deployment with documented threat model',
    fa: 'استقرارِ تقویت‌شده با مدلِ تهدیدِ مستند',
  },
  'pricing.tier.sovereign.f5': {
    en: 'Dedicated onboarding engineer',
    fa: 'مهندسِ پذیرشِ اختصاصی',
  },
  'pricing.tier.sovereign.f6': {
    en: 'Priority support with named contact',
    fa: 'پشتیبانیِ اولویت‌دار با رابطِ نام‌برده',
  },
} satisfies StringTable<string>;

type Tier = {
  name: keyof typeof STRINGS extends infer K
    ? K extends `pricing.tier.${infer T}.name`
      ? T
      : never
    : never;
  recommended?: boolean;
  href: string;
};

const tiers: Tier[] = [
  {
    name: 'analyst',
    recommended: true,
    href: `${PORTAL_URL}/sign-up`,
  },
  {
    name: 'sovereign',
    href: `mailto:${CONTACT_EMAIL}`,
  },
];

const featureKeys = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6'] as const;

export function Pricing() {
  const lang = useLang();

  return (
    <section
      id="pricing"
      className="border-t border-[var(--color-border)] bg-[var(--color-canvas)]"
    >
      <div className="mx-auto max-w-[88rem] px-6 py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="mb-4 text-xs uppercase tracking-wider text-[var(--color-ink-muted)]">
            {t(STRINGS, 'pricing.eyebrow', lang)}
          </div>
          <h2 className="text-3xl leading-tight md:text-5xl">
            {t(STRINGS, 'pricing.title.lead', lang)}{' '}
            <span className="text-[var(--color-accent)]">
              {t(STRINGS, 'pricing.title.accent', lang)}
            </span>
            {t(STRINGS, 'pricing.title.tail', lang)}
          </h2>
          <p className="mt-4 text-[var(--color-ink-secondary)] md:text-lg">
            {t(STRINGS, 'pricing.subtitle', lang)}
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {tiers.map((tier) => {
            const k = (suffix: string): keyof typeof STRINGS =>
              `pricing.tier.${tier.name}.${suffix}` as keyof typeof STRINGS;
            return (
              <article
                key={tier.name}
                className={
                  'glass-card relative flex flex-col rounded-md p-8 ' +
                  (tier.recommended
                    ? 'border-[color-mix(in_oklab,var(--color-accent)_65%,transparent)] shadow-[0_0_0_1px_color-mix(in_oklab,var(--color-accent)_45%,transparent),0_16px_48px_-24px_color-mix(in_oklab,var(--color-accent)_55%,transparent)]'
                    : '')
                }
              >
                {tier.recommended && (
                  <div className="absolute -top-3 start-6 rounded-xs bg-[var(--color-accent)] px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-[var(--color-canvas)]">
                    {t(STRINGS, 'pricing.recommended', lang)}
                  </div>
                )}

                <header>
                  <h3 className="text-2xl">{t(STRINGS, k('name'), lang)}</h3>
                  <p className="mt-1 text-sm text-[var(--color-ink-secondary)]">
                    {t(STRINGS, k('tagline'), lang)}
                  </p>
                </header>

                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-serif text-4xl">
                    {t(STRINGS, k('price'), lang)}
                  </span>
                  <span className="text-xs uppercase tracking-wider text-[var(--color-ink-muted)]">
                    {t(STRINGS, k('note'), lang)}
                  </span>
                </div>

                <ul className="mt-6 flex flex-col gap-3">
                  {featureKeys.map((fk) => (
                    <li
                      key={fk}
                      className="flex items-start gap-3 text-sm text-[var(--color-ink-secondary)]"
                    >
                      <Check className="mt-0.5 size-4 shrink-0 text-[var(--color-accent-bright)]" />
                      <span>{t(STRINGS, k(fk), lang)}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={tier.href}
                  target={tier.href.startsWith('mailto:') ? undefined : '_self'}
                  className={
                    'mt-8 inline-flex items-center justify-center gap-2 rounded-sm px-5 py-2.5 text-sm font-medium transition-colors ' +
                    (tier.recommended
                      ? 'bg-[var(--color-ink)] text-[var(--color-canvas)] hover:bg-[var(--color-accent)]'
                      : 'border border-[var(--color-border)] bg-[var(--color-canvas)] text-[var(--color-ink)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]')
                  }
                >
                  {t(STRINGS, k('cta'), lang)}
                  <ArrowRight className="icon-directional size-3.5" />
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
